import type { CustomerRevenueReportItem, RevenuePerMonthBreakdown } from '@weft/types';
import { DEFAULT_CURRENCY, type CustomerRevenueReport } from '@weft/types';
import type { ItemOf } from '@weft/utils';
import { dayjs, fillArrayOfLength, isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import type { Borders, Font } from 'exceljs';
import { sortBy } from 'lodash';
import { getCustomerRevenueDateMap } from './get-revenue-date-map';
import { getTotalCustomerRevenueDateHeaders } from './get-total-revenue-dates';

const INVOICE_ROW_GAP = 3;

const revenueRevenueKey: keyof RevenuePerMonthBreakdown = 'deferredRevenue';
const revenueTitles = ['Revenue', 'Deferred', 'Unbilled'];
const revenueKeys: (keyof RevenuePerMonthBreakdown)[] = ['revenue', 'deferredRevenue', 'unbilledRevenue'];
export type CustomerRevenueExcelRowType = { value: string | number | undefined, border?: Partial<Borders>, font?: Partial<Font> };

export function formatCustomerRevenueReport(report: CustomerRevenueReport) {
  const revenueHeaders = getTotalCustomerRevenueDateHeaders(report.reportItems);

  return report.reportItems.flatMap((reportItem) => {
    return getCustomerRevenueItemRows(reportItem, revenueHeaders);
  });
}

function getCustomerRevenueItemRows(reportItem: CustomerRevenueReportItem, revenueHeaders: string[]): CustomerRevenueExcelRowType[][] {
  const invoicesToDisplay = sortInvoicesByDate(reportItem.invoices) ?? [];
  const itemsToDisplay = Math.max(invoicesToDisplay.length + INVOICE_ROW_GAP + 1, revenueTitles.length) + 1;

  return Array.from({ length: itemsToDisplay }).map((_, index) => {
    const revenueKey = index <= 2 ? revenueKeys[index] : revenueRevenueKey;
    const revenueSource = index <= 2 ? reportItem.yearlyRevenue : invoicesToDisplay[index - INVOICE_ROW_GAP - 1]?.yearlyRevenue;
    const formattedRevenuePerMonth = getCustomerRevenueDateMap(revenueSource ?? reportItem.yearlyRevenue, revenueKey, reportItem.customer.currency ?? DEFAULT_CURRENCY);
    const formattedRevenuePerMonthRows = getFormattedRevenuePerMonthRows(formattedRevenuePerMonth, revenueHeaders, index, itemsToDisplay);

    const staticRows = getStaticHeaders(reportItem);

    const rowsToDisplay = index === 0 ? staticRows : [{ value: '' }];
    const invoiceRowsToDisplay = getInvoiceRowsToDisplay(invoicesToDisplay, index);

    const gapRows = getGapRows(index > INVOICE_ROW_GAP);
    const revenueRow = getRevenueRow(index, itemsToDisplay);

    return [
      ...rowsToDisplay,
      ...invoiceRowsToDisplay,
      ...gapRows,
      revenueRow,
      ...formattedRevenuePerMonthRows,
    ];
  });
}

function getRevenueRow(index: number, itemsToDisplay: number) {
  const revenueTitle = index <= INVOICE_ROW_GAP ? revenueTitles[index] : 'Deferred';
  const isEmptyCell = (index === itemsToDisplay - 1 && itemsToDisplay > 2) || index === INVOICE_ROW_GAP;

  const value = isEmptyCell ? '' : revenueTitle;
  const border = isEmptyCell ? {} : { right: { style: 'thin' }, bottom: { style: 'thin' } };

  return { value, border };
}

function getGapRows(isInvoiceRows: boolean) {
  return fillArrayOfLength(isInvoiceRows ? 2 : 1, { value: '' });
}

function getInvoiceRowsToDisplay(invoices: NonNullable<CustomerRevenueReportItem['invoices']>, rowIndex: number) {
  const invoiceRows = rowIndex === INVOICE_ROW_GAP ? getInvoiceHeadersToDisplay() : getInvoiceRows(invoices[rowIndex - INVOICE_ROW_GAP - 1]);

  return rowIndex >= INVOICE_ROW_GAP 
    ? invoiceRows 
    : fillArrayOfLength(rowIndex === 0 ? 0 : invoiceRows.length + 1, { value: '' });
}

function getInvoiceHeadersToDisplay(): CustomerRevenueExcelRowType[] {
  return [
    { value: 'Invoice ID', font: { bold: true }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' } } },
    { value: 'Invoice date', font: { bold: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' } } },
    { value: 'Invoice amount', font: { bold: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } },
  ];
}

function getInvoiceRows(invoice: ItemOf<NonNullable<CustomerRevenueReportItem['invoices']>> | undefined) {
  const invoiceDate = isNotNullOrUndefined(invoice) ? formatReportDate(invoice.period.startTime) : undefined;

  return [
    { value: invoice?.id },
    { value: invoiceDate },
    { value: invoice?.amount },
  ];
}

function getStaticHeaders(reportItem: CustomerRevenueReportItem): CustomerRevenueExcelRowType[] {
  const productName = reportItem.productVariant?.name ?? reportItem.revisionedLineItem?.name;
  const productType = reportItem.productVariant?.pricing.pricingModel ?? reportItem.revisionedLineItem?.type;
  const contractStartDate = isNotNullOrUndefined(reportItem.contract) 
    ? formatReportDate(reportItem.contract.startDate)
    : undefined;
  const contractEndDate = isNotNullOrUndefined(reportItem.contract) 
    ? getFormattedContractEndDate(reportItem.contract.endDate)
    : undefined;  

  return [
    { value: reportItem.customer.name, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' } } },
    { value: contractStartDate, border: { top: { style: 'thin' }, bottom: { style: 'thin' } } },
    { value: contractEndDate, border: { top: { style: 'thin' }, bottom: { style: 'thin' } } },
    { value: productName, border: { top: { style: 'thin' }, bottom: { style: 'thin' } } },
    { value: productType, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } },
  ];
}

function getFormattedContractEndDate(contractEndDate: Date | undefined) {
  return isNullOrUndefined(contractEndDate) ? 'Unlimited' : formatReportDate(contractEndDate);
}

function formatReportDate(date: Date) {
  return dayjs(date).format('YYYY-MM-DD');
}

function getFormattedRevenuePerMonthRows(
  formattedRevenuePerMonth: Map<string, string>,
  revenueHeaders: string[],
  rowIndex: number,
  rowsToDisplay: number,
): CustomerRevenueExcelRowType[] {
  const isLastRow = rowIndex === rowsToDisplay - 1 && rowsToDisplay > 3;
  const isNotGapRow = rowIndex <= 2 || rowIndex >= 1 + INVOICE_ROW_GAP;

  if (isLastRow || !isNotGapRow) {
    return [];
  }

  return revenueHeaders.map((header ) => {
    const revenue = formattedRevenuePerMonth.get(header);

    return { value: revenue ?? '', border: { top: { style: 'thin' }, bottom: { style: 'thin' } } };
  });
}

function sortInvoicesByDate(invoices: CustomerRevenueReportItem['invoices']) {
  return sortBy(invoices, (invoice) => invoice.period.startTime.getTime()).reverse();
}